/**
 * User Settings Service
 *
 * Generic service for persisting user preferences to PostgreSQL backend.
 * Provides localStorage fallback for offline/error scenarios.
 *
 * Features:
 * - Typed get/set operations with generics
 * - Namespace support for logical grouping
 * - Automatic localStorage sync for offline resilience
 * - Background sync when coming online
 *
 * Usage:
 *   import { getSetting, setSetting } from '@shared/services/userSettingsService';
 *
 *   // Get a setting with type
 *   const colors = await getSetting<Record<string, string>>('epg-custom-colors');
 *
 *   // Set a setting
 *   await setSetting('kanban-view-settings', { cardSize: 'compact' });
 */

import { apiClient } from './apiClient';
import { logger } from '@shared/utils/logger';

const LOCAL_STORAGE_PREFIX = 'bo360-settings-';
const DEFAULT_NAMESPACE = 'default';

/**
 * Check if user is authenticated.
 *
 * CRITICAL: This is used to prevent pre-auth API calls from triggering token refresh loops.
 * Settings operations should silently fail and use localStorage fallback when not authenticated.
 */
const isAuthenticated = (): boolean => {
  if (typeof window === 'undefined') return false;

  const authData = localStorage.getItem('dms-auth-storage');
  if (authData) {
    try {
      const parsed = JSON.parse(authData);
      return parsed.state?.isAuthenticated === true;
    } catch {
      return false;
    }
  }
  return false;
};

interface SettingResponse<T> {
  key: string;
  value: T | null;
  namespace: string | null;
}

interface SettingPayload<T> {
  value: T;
}

/**
 * Get a setting from PostgreSQL with localStorage fallback.
 *
 * @param key - Setting key (e.g., 'kanban-view-settings')
 * @param options - Optional configuration
 * @returns Setting value or null if not found
 *
 * @example
 * const colors = await getSetting<Record<string, string>>('epg-custom-colors');
 * const presets = await getSetting<FilterPreset[]>('epg-filter-presets', { namespace: 'epg' });
 */
export async function getSetting<T>(
  key: string,
  options: {
    namespace?: string;
    fallbackToLocal?: boolean;
    defaultValue?: T;
  } = {}
): Promise<T | null> {
  const { namespace = DEFAULT_NAMESPACE, fallbackToLocal = true, defaultValue = null } = options;
  const localKey = `${LOCAL_STORAGE_PREFIX}${namespace}-${key}`;

  // CRITICAL FIX: Skip API call if not authenticated
  // This prevents pre-auth API calls from triggering token refresh infinite loops
  // Silently use localStorage fallback instead
  if (!isAuthenticated()) {
    logger.log(`[UserSettingsService] Not authenticated - using localStorage for "${key}"`);
    if (fallbackToLocal) {
      try {
        const cached = localStorage.getItem(localKey);
        if (cached) {
          return JSON.parse(cached) as T;
        }
      } catch {
        // Ignore parse errors
      }
    }
    return defaultValue;
  }

  try {
    const response = await apiClient.get<SettingResponse<T>>(
      `/app-settings/${encodeURIComponent(key)}`,
      { params: { namespace } }
    );

    const value = response.data.value;

    // Update localStorage cache on success
    if (value !== null) {
      try {
        localStorage.setItem(localKey, JSON.stringify(value));
      } catch {
        // Ignore localStorage errors (quota, private mode)
      }
    }

    // Return value if found, otherwise return defaultValue
    // This handles the case where API returns null (setting doesn't exist yet)
    return value !== null ? value : defaultValue;
  } catch (error) {
    // Check if it's a transaction error that might be retryable
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (errorMessage.includes('InFailedSqlTransaction') || errorMessage.includes('current transaction is aborted')) {
      logger.warn(`[UserSettingsService] Database transaction error for "${key}", using fallback`);
    } else {
      logger.error(`[UserSettingsService] Failed to get "${key}":`, error);
    }

    // Fallback to localStorage
    if (fallbackToLocal) {
      try {
        const cached = localStorage.getItem(localKey);
        if (cached) {
          logger.log(`[UserSettingsService] Using localStorage fallback for "${key}"`);
          return JSON.parse(cached) as T;
        }
      } catch {
        // Ignore parse errors
      }
    }

    // Return default value if provided
    if (defaultValue !== null) {
      logger.log(`[UserSettingsService] Using default value for "${key}"`);
      return defaultValue;
    }

    return null;
  }
}

/**
 * Set a setting in PostgreSQL with localStorage sync.
 *
 * @param key - Setting key
 * @param value - Value to store (must be JSON-serializable)
 * @param options - Optional configuration
 * @returns True if saved successfully
 *
 * @example
 * await setSetting('kanban-view-settings', { cardSize: 'compact', showEmptyColumns: true });
 * await setSetting('epg-filter-presets', presets, { namespace: 'epg' });
 */
export async function setSetting<T>(
  key: string,
  value: T,
  options: {
    namespace?: string;
    syncToLocal?: boolean;
  } = {}
): Promise<boolean> {
  const { namespace = DEFAULT_NAMESPACE, syncToLocal = true } = options;
  const localKey = `${LOCAL_STORAGE_PREFIX}${namespace}-${key}`;

  // Always update localStorage first (optimistic update)
  if (syncToLocal) {
    try {
      localStorage.setItem(localKey, JSON.stringify(value));
    } catch {
      // Ignore localStorage errors
    }
  }

  // CRITICAL FIX: Skip API call if not authenticated
  // This prevents pre-auth API calls from triggering token refresh infinite loops
  // localStorage was already updated above, so settings are preserved locally
  if (!isAuthenticated()) {
    logger.log(`[UserSettingsService] Not authenticated - skipping cloud sync for "${key}"`);
    return true; // Return true since localStorage was updated successfully
  }

  try {
    const payload: SettingPayload<T> = { value };
    await apiClient.put(
      `/app-settings/${encodeURIComponent(key)}`,
      payload,
      { params: { namespace } }
    );

    logger.log(`[UserSettingsService] Saved "${key}" to PostgreSQL`);
    return true;
  } catch (error) {
    logger.error(`[UserSettingsService] Failed to save "${key}":`, error);

    // Value is still in localStorage, will sync later
    if (syncToLocal) {
      queueForSync(key, namespace);
    }

    return false;
  }
}

/**
 * Delete a setting from PostgreSQL and localStorage.
 *
 * @param key - Setting key to delete
 * @param options - Optional configuration
 * @returns True if deleted successfully
 */
export async function deleteSetting(
  key: string,
  options: {
    namespace?: string;
  } = {}
): Promise<boolean> {
  const { namespace = DEFAULT_NAMESPACE } = options;
  const localKey = `${LOCAL_STORAGE_PREFIX}${namespace}-${key}`;

  // Remove from localStorage first
  try {
    localStorage.removeItem(localKey);
  } catch {
    // Ignore localStorage errors
  }

  try {
    await apiClient.delete(
      `/app-settings/${encodeURIComponent(key)}`,
      { params: { namespace } }
    );

    logger.log(`[UserSettingsService] Deleted "${key}"`);
    return true;
  } catch (error) {
    logger.error(`[UserSettingsService] Failed to delete "${key}":`, error);
    return false;
  }
}

/**
 * Get all settings in a namespace.
 *
 * @param namespace - Namespace to retrieve
 * @returns Dictionary of key -> value pairs
 */
export async function getNamespaceSettings<T = unknown>(
  namespace: string
): Promise<Record<string, T>> {
  try {
    const response = await apiClient.get<{ namespace: string; settings: Record<string, T> }>(
      `/app-settings/namespace/${encodeURIComponent(namespace)}`
    );

    return response.data.settings;
  } catch (error) {
    logger.error(`[UserSettingsService] Failed to get namespace "${namespace}":`, error);
    return {};
  }
}

// ==================== Sync Queue (Background Sync) ====================

const SYNC_QUEUE_KEY = `${LOCAL_STORAGE_PREFIX}sync-queue`;

interface SyncQueueItem {
  key: string;
  namespace: string;
  timestamp: number;
}

/**
 * Add a setting to the sync queue for later retry.
 */
function queueForSync(key: string, namespace: string): void {
  try {
    const queue = getSyncQueue();
    const item: SyncQueueItem = { key, namespace, timestamp: Date.now() };

    // Remove existing entry for same key
    const filtered = queue.filter((q) => !(q.key === key && q.namespace === namespace));
    filtered.push(item);

    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(filtered));
    logger.log(`[UserSettingsService] Queued "${key}" for background sync`);
  } catch {
    // Ignore queue errors
  }
}

/**
 * Get the current sync queue.
 */
function getSyncQueue(): SyncQueueItem[] {
  try {
    const stored = localStorage.getItem(SYNC_QUEUE_KEY);
    return stored ? (JSON.parse(stored) as SyncQueueItem[]) : [];
  } catch {
    return [];
  }
}

/**
 * Process sync queue - call when coming online.
 */
export async function processSyncQueue(): Promise<void> {
  const queue = getSyncQueue();
  if (queue.length === 0) return;

  logger.log(`[UserSettingsService] Processing ${queue.length} queued settings`);

  for (const item of queue) {
    const localKey = `${LOCAL_STORAGE_PREFIX}${item.namespace}-${item.key}`;
    try {
      const cached = localStorage.getItem(localKey);
      if (cached) {
        const value = JSON.parse(cached);
        await setSetting(item.key, value, {
          namespace: item.namespace,
          syncToLocal: false, // Already in localStorage
        });
      }
    } catch (error) {
      logger.error(`[UserSettingsService] Failed to sync "${item.key}":`, error);
    }
  }

  // Clear queue after processing
  try {
    localStorage.removeItem(SYNC_QUEUE_KEY);
  } catch {
    // Ignore
  }
}

// Process queue when coming online
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    logger.log('[UserSettingsService] Online - processing sync queue');
    processSyncQueue();
  });
}

// Export default object for convenience
export default {
  getSetting,
  setSetting,
  deleteSetting,
  getNamespaceSettings,
  processSyncQueue,
};